import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Clock, Coins, Gamepad2, ArrowRight, Receipt, RefreshCw, Sparkles } from "lucide-react";
import { LiveStats } from "@/components/LiveStats";
import { TopScores, ChampionsBanner } from "@/components/Leaderboard";
import { FriendsCard } from "@/components/Friends";

const TIER_COLOR = { bronze: "#E08D3C", silver: "#C7D2DE", gold: "#F5C842" };

function daysLeft(iso) {
  const ms = new Date(iso) - new Date();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [tier, setTier] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = () => Promise.all([api.get("/subscription"), api.get("/invoices")])
    .then(([s, inv]) => { setSub(s.data.subscription); setTier(s.data.tier); setInvoices(inv.data.invoices); })
    .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const cancelRenewal = async () => {
    setCancelling(true);
    try {
      await api.post("/payments/cancel-renewal");
      toast.success("Auto-renewal cancelled. You keep access until it expires.");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCancelling(false);
    }
  };

  const active = !!sub;
  const color = tier ? TIER_COLOR[tier.id] : "#00F0FF";

  return (
    <div className="min-h-screen bg-[#0A0A14]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <BackButton className="mb-6" />
        <p className="font-arcade text-xl text-[#FF006E] mb-1">// PLAYER PROFILE</p>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <h1 className="font-head font-900 text-3xl sm:text-4xl text-slate-50">WELCOME, {user?.name?.toUpperCase()}</h1>
          <LiveStats />
        </div>

        <ChampionsBanner className="mb-6" />
        <TopScores className="mb-8" />

        <Link
          to="/alignment"
          data-testid="dashboard-alignment-card"
          className="group relative block mb-8 rounded-2xl border border-violet-500/25 overflow-hidden transition-transform duration-300 hover:-translate-y-0.5"
          style={{ background: "radial-gradient(700px 300px at 15% 0%, rgba(88,28,135,0.4), transparent 60%), radial-gradient(600px 300px at 95% 100%, rgba(14,116,144,0.3), transparent 60%), #0d0a1a" }}
        >
          <div className="flex flex-wrap items-center justify-between gap-4 p-7">
            <div>
              <div className="flex items-center gap-2 mb-2 text-violet-300"><Sparkles className="w-5 h-5" /><span className="font-arcade text-lg tracking-wider">ALIGNMENT</span></div>
              <h2 className="font-head font-700 text-2xl text-slate-50">Play with purpose</h2>
              <p className="text-sm text-slate-400 mt-1 max-w-lg">A daily reflection awaits. Learn, breathe, and build mindful habits across your spiritual worlds.</p>
            </div>
            <span className="inline-flex items-center gap-2 font-arcade text-lg px-6 py-3 bg-[#A78BFA] text-black border border-[#A78BFA] group-hover:shadow-[4px_4px_0px_#2DD4BF] transition-shadow duration-300">
              ENTER ALIGNMENT <ArrowRight className="w-5 h-5" />
            </span>
          </div>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Subscription status card */}
          <div className="lg:col-span-2 bg-[#11111A] border border-cyan-500/20 p-8" data-testid="subscription-status-card">
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="w-6 h-6" style={{ color }} />
              <h2 className="font-head font-700 text-xl text-slate-50">SUBSCRIPTION</h2>
            </div>

            {loading ? (
              <p className="font-arcade text-lg text-slate-500">LOADING…</p>
            ) : active ? (
              <div>
                <div className="flex flex-wrap items-end gap-4 mb-8">
                  <span className="font-head font-900 text-5xl" style={{ color }}>{tier.name.toUpperCase()}</span>
                  <span className="font-arcade text-xl text-slate-400">ACTIVE</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="border border-slate-800 p-5">
                    <Clock className="w-5 h-5 text-[#00F0FF] mb-2" />
                    <p className="font-head font-700 text-2xl text-slate-50" data-testid="days-left">{daysLeft(sub.expires_at)} DAYS</p>
                    <p className="text-xs text-slate-500 mt-1">Expires {new Date(sub.expires_at).toLocaleDateString()}</p>
                  </div>
                  <div className="border border-slate-800 p-5">
                    <Gamepad2 className="w-5 h-5 text-[#F5C842] mb-2" />
                    <p className="font-head font-700 text-2xl text-slate-50">{tier.level === 3 ? "ALL" : tier.level} GAMES</p>
                    <p className="text-xs text-slate-500 mt-1">Unlocked at your tier</p>
                  </div>
                </div>

                {/* Auto-renewal status */}
                <div className="mt-6 flex items-center gap-2 border border-slate-800 p-4" data-testid="renewal-status">
                  <RefreshCw className={`w-5 h-5 ${sub.auto_renew ? "text-[#00F0FF]" : "text-slate-600"}`} />
                  <div className="flex-1">
                    {sub.auto_renew ? (
                      <p className="font-arcade text-base text-[#00F0FF]">AUTO-RENEWS on {new Date(sub.expires_at).toLocaleDateString()}</p>
                    ) : sub.cancel_at_period_end ? (
                      <p className="font-arcade text-base text-[#F5C842]">RENEWAL CANCELLED · access until {new Date(sub.expires_at).toLocaleDateString()}</p>
                    ) : (
                      <p className="font-arcade text-base text-slate-400">ONE-TIME · no auto-renewal</p>
                    )}
                  </div>
                  {sub.auto_renew && (
                    <button
                      data-testid="cancel-renewal-btn"
                      onClick={cancelRenewal}
                      disabled={cancelling}
                      className="font-arcade text-sm px-4 py-2 border border-[#FF006E]/50 text-[#FF006E] hover:bg-[#FF006E] hover:text-black transition-colors duration-200 disabled:opacity-50"
                    >
                      {cancelling ? "…" : "CANCEL RENEWAL"}
                    </button>
                  )}
                </div>

                <Link to="/games" data-testid="go-to-games-btn" className="mt-8 inline-flex items-center gap-2 font-arcade text-xl px-6 py-3 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200">
                  ENTER ARCADE <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-end gap-4 mb-4">
                  <span className="font-head font-900 text-5xl text-[#C7D2DE]">FREE PLAYER</span>
                  <span className="font-arcade text-xl text-slate-400">ACTIVE</span>
                </div>
                <p className="text-sm text-slate-500 mb-8 max-w-md">Snake & Brick Buster are free to play. Unlock the Gold vault with an on-chain BTC subscription.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/games" data-testid="play-free-btn" className="inline-flex items-center gap-2 font-arcade text-xl px-6 py-3 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200">
                    PLAY FREE GAMES <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/pay/gold" data-testid="choose-plan-btn" className="inline-flex items-center gap-2 font-arcade text-xl px-6 py-3 bg-[#F5C842] text-black border border-[#F5C842] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200">
                    UNLOCK GOLD <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Subscriptions */}
          <div className="bg-[#11111A] border border-cyan-500/20 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Coins className="w-6 h-6 text-[#F5C842]" />
              <h2 className="font-head font-700 text-xl text-slate-50">SUBSCRIPTIONS</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Snake & Brick Buster are free. Unlock Gold with a BTC subscription.</p>
            <div className="space-y-3">
              <Link to="/pay/silver" data-testid="upgrade-silver" className="flex items-center justify-between px-4 py-3 border border-slate-800 hover:border-[#C7D2DE] transition-colors duration-200 group">
                <span className="font-arcade text-lg text-[#C7D2DE]">SILVER · 0.00015 BTC</span>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#C7D2DE] transition-colors duration-200" />
              </Link>
              <Link to="/pay/gold" data-testid="upgrade-gold" className="flex items-center justify-between px-4 py-3 border border-slate-800 hover:border-[#F5C842] transition-colors duration-200 group">
                <span className="font-arcade text-lg text-[#F5C842]">GOLD · 0.0002 BTC</span>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#F5C842] transition-colors duration-200" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <FriendsCard />
        </div>

        {/* Payment history */}
        <div className="mt-8 bg-[#11111A] border border-cyan-500/20 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Receipt className="w-6 h-6 text-[#00F0FF]" />
            <h2 className="font-head font-700 text-xl text-slate-50">PAYMENT HISTORY</h2>
          </div>
          {invoices.length === 0 ? (
            <p className="font-arcade text-lg text-slate-500">NO INVOICES YET</p>
          ) : (
            <div className="space-y-2" data-testid="invoice-list">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border border-slate-800 text-sm">
                  <span className="font-arcade text-lg" style={{ color: TIER_COLOR[inv.tier] }}>{inv.tier.toUpperCase()}</span>
                  <span className="text-slate-400">{inv.expected_btc} BTC</span>
                  <span className={`font-arcade text-base ${inv.status === "confirmed" ? "text-[#00F0FF]" : inv.status === "detected" ? "text-[#F5C842]" : "text-slate-500"}`}>
                    {inv.status.toUpperCase()}
                  </span>
                  <span className="text-slate-600 text-xs">{new Date(inv.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
